import { I18n } from '@calendly/i18n';
import { Banner } from '@calendly/ui/components/banner';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common';
import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade';
import { rawFeatureTierSelector } from 'features/billing';
import { useEnableNotetakerWithLoader, useNotetakerFreeMeetingsQuota } from 'features/meeting_intelligence';
import { TRACKING_EVENT_NAMES, useTrackOnNavigation } from 'hooks/analytics_events';
import { useNotetakerUpsellUIVisible } from 'hooks/notetaker_visibility';
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment';
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment';
import { showUpgradeNotetakerSpotlight } from './upgrade_notetaker_spotlight_slice';
const ANALYTICS_PAYWALL_LOCATION = {
  recaps: 'recaps_page',
  meetings: 'meetings_page'
};
const buttonsContainerIsPhoneCss = "bbt6o61";
const buttonsContainerCss = "bloulnq";
const flexItemCss = "fhvlbd4";
const ButtonsContainer = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <div ref={ref} {...props} className={classNames(buttonsContainerCss, {
    [buttonsContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const notetakerGhostButtonCss = "n5pitvo";
const NotetakerGhostButton = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <BareButton ref={ref} {...props} className={classNames(notetakerGhostButtonCss, {
    [flexItemCss]: isPhone
  }, props.className)} />;
});
const UpgradeButton = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Button ref={ref} {...props} className={classNames({
    [flexItemCss]: isPhone
  }, props.className)} />;
});
const Footer = ({
  handleDismiss,
  handleUpgrade
}) => <ButtonsContainer>
    <NotetakerGhostButton decoration="ghost" onClick={handleDismiss} size="normal">
      {I18n.t('publisher.upgrade_banner.notetaker.dismiss')}
    </NotetakerGhostButton>
    <UpgradeButton decoration="meeting-intelligence" responsive={false} onClick={handleUpgrade}>
      {I18n.t('publisher.upgrade_banner.notetaker.upgrade_plan')}
    </UpgradeButton>
  </ButtonsContainer>;
export const DismissableNotetakerBanner = ({
  bannerType,
  featureKey = 'meeting_intelligence',
  onSuccess,
  isInNotetakerFreeExperiment = false
}) => {
  const dispatch = useDispatch();
  const {
    isVisible: upsellUiVisible,
    isLoading: isUpsellUiVisibilityLoading
  } = useNotetakerUpsellUIVisible();
  const shouldCheckMeteredExperiment = upsellUiVisible && !isUpsellUiVisibilityLoading;
  const {
    isInExperiment: isInNotetakerFiveFreeRecapsExperiment,
    isLoading: isNotetakerFiveFreeRecapsExperimentLoading
  } = useNotetakerFiveFreeRecapsExperiment(shouldCheckMeteredExperiment);
  const {
    isInExperiment: isInResolvedNotetakerFreeExperiment,
    isLoading: isNotetakerFreeExperimentLoading
  } = useNotetakerFreeExperiment();
  const isInLegacyMeteredNotetakerExperiment = isInNotetakerFreeExperiment || isInResolvedNotetakerFreeExperiment;
  const isInMeteredNotetakerExperiment = isInNotetakerFiveFreeRecapsExperiment || isInLegacyMeteredNotetakerExperiment;
  const isMeteredExperimentLoading = shouldCheckMeteredExperiment && !isInMeteredNotetakerExperiment && (isNotetakerFiveFreeRecapsExperimentLoading || !isInNotetakerFreeExperiment && isNotetakerFreeExperimentLoading);
  const {
    data: userData,
    isSuccess: isUserSuccess
  } = useGetUserQuery();
  const {
    notetakerQuotaData
  } = useNotetakerFreeMeetingsQuota({
    skip: !isInMeteredNotetakerExperiment
  });
  const [updateActivationData] = useUpdateUserActivationDataMutation();
  const enableNotetaker = useEnableNotetakerWithLoader();
  const enforceUpgrade = useEnforceUpgrade(featureKey);
  const requiredTier = useSelector(state => rawFeatureTierSelector(state, {
    featureKey
  }));
  const isOwnerOrAdmin = userData?.organization_membership?.is_admin_or_owner;
  const isBannerDismissed = userData?.activation_data?.banners?.[`${bannerType}_notetaker_upgrade_banner_dismissed`];
  const hasReachedFreeMeetingsQuota = isInMeteredNotetakerExperiment ? notetakerQuotaData?.resource?.featureAvailable === false : true;
  const isUpsellBannerVisible = upsellUiVisible && !isUpsellUiVisibilityLoading;
  const shouldShowBanner = isUpsellBannerVisible && !isMeteredExperimentLoading && hasReachedFreeMeetingsQuota && !isBannerDismissed && isUserSuccess && !!userData;
  const shouldTrack = shouldShowBanner && !!requiredTier;
  const trackingConfig = useMemo(() => ({
    name: TRACKING_EVENT_NAMES.PAYWALL_SHOWN,
    version: 1,
    dependencies: {
      user_id: userData?.id
    },
    entities: ['attribution', 'organization', 'user'],
    properties: {
      paywall_feature: 'notetaker_upgrade_plan_1',
      paywall_type: 'inline',
      plan_required: requiredTier,
      location: ANALYTICS_PAYWALL_LOCATION[bannerType]
    }
  }), [userData?.id, requiredTier, bannerType]);
  useTrackOnNavigation(shouldTrack, trackingConfig);
  if (!shouldShowBanner) {
    return null;
  }
  const handleDismiss = () => {
    updateActivationData({
      banners: {
        [`${bannerType}_notetaker_upgrade_banner_dismissed`]: true
      }
    });
    dispatch(showUpgradeNotetakerSpotlight());
  };

  // Default onSuccess: enable notetaker with loader and redirect to meetings page
  // Can be overridden by passing a custom function, or disabled by passing null
  const defaultOnSuccess = enableNotetaker;
  const finalOnSuccess = onSuccess === null ? undefined : onSuccess || defaultOnSuccess;
  const handleUpgrade = () => {
    enforceUpgrade(finalOnSuccess);
  };
  return <Banner type="upgrade" isHorizontal footer={<Footer handleDismiss={handleDismiss} handleUpgrade={handleUpgrade} />} spacingBottom={16}>
      <div>
        <BodyText weight="bold" size="large">
          {I18n.t(`publisher.upgrade_banner.notetaker.${bannerType}.title`)}
        </BodyText>
        <BodyText size="large">
          {isOwnerOrAdmin ? I18n.t('publisher.upgrade_banner.notetaker.body') : I18n.t('publisher.upgrade_banner.notetaker.non_admin_body')}
        </BodyText>
      </div>
    </Banner>;
};

require("./dismissable_notetaker_banner.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./dismissable_notetaker_banner.js");